import { useEffect, useMemo, useState } from 'react';
import { exportLedgerEntriesCsv, listLedgerEntries } from '../../api/adminFinanceApi';

const referenceTypes = ['', 'PAYMENT', 'PAYOUT', 'REFUND', 'ADJUSTMENT', 'SETTLEMENT'];
const ownerTypes = ['', 'PLATFORM', 'HOST', 'GUEST', 'NONE'];

const formatMoney = (currency, paise) => {
  const amount = Number(paise || 0) / 100;
  if (!Number.isFinite(amount)) return `${currency || ''} 0`.trim();
  return `${currency || ''} ${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const AdminLedgerEntries = ({ token }) => {
  const [filters, setFilters] = useState({
    referenceType: '',
    ownerType: '',
    ownerId: '',
    accountCode: '',
    fromDate: '',
    toDate: '',
  });
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await listLedgerEntries(token, { ...filters, page, limit: pagination.limit });
      setItems(res.items || []);
      setPagination({
        page: res.page || page,
        limit: res.limit || pagination.limit,
        total: res.total || 0,
      });
    } catch (err) {
      setError(err.message || 'Failed to load ledger entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => load(1);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportLedgerEntriesCsv(token, { ...filters, limit: 5000 });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ledger-entries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to export ledger entries');
    } finally {
      setExporting(false);
    }
  };

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 20)));
  }, [pagination]);

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Finance</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Ledger Entries</h1>
          <p className="text-sm text-slate-600">Audit trail of every finance movement.</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-6">
        <select
          value={filters.referenceType}
          onChange={(e) => setFilters((f) => ({ ...f, referenceType: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {referenceTypes.map((opt) => (
            <option key={opt || 'all'} value={opt}>
              {opt || 'All types'}
            </option>
          ))}
        </select>
        <select
          value={filters.ownerType}
          onChange={(e) => setFilters((f) => ({ ...f, ownerType: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {ownerTypes.map((opt) => (
            <option key={opt || 'all'} value={opt}>
              {opt || 'All owners'}
            </option>
          ))}
        </select>
        <input
          type="number"
          placeholder="Owner ID"
          value={filters.ownerId}
          onChange={(e) => setFilters((f) => ({ ...f, ownerId: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="text"
          placeholder="Account code"
          value={filters.accountCode}
          onChange={(e) => setFilters((f) => ({ ...f, accountCode: e.target.value.toUpperCase() }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.fromDate}
          onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.toDate}
          onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 md:col-span-6"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Debit</th>
              <th className="px-4 py-3">Credit</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Memo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-rose-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  No ledger entries found.
                </td>
              </tr>
            )}
            {items.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-600">{formatDateTime(row.created_at || row.createdAt)}</td>
                <td className="px-4 py-3">
                  <div className="text-sm font-semibold text-slate-900">{row.reference_type}</div>
                  <div className="text-xs text-slate-500">#{row.reference_id || 'N/A'}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <div className="text-sm font-semibold">{row.debit_account_code || row.debitAccountCode}</div>
                  <div className="text-xs text-slate-500">
                    {(row.debit_owner_type || row.debitOwnerType || 'NONE').toString()}
                    {row.debit_owner_id || row.debitOwnerId ? ` #${row.debit_owner_id || row.debitOwnerId}` : ''}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <div className="text-sm font-semibold">{row.credit_account_code || row.creditAccountCode}</div>
                  <div className="text-xs text-slate-500">
                    {(row.credit_owner_type || row.creditOwnerType || 'NONE').toString()}
                    {row.credit_owner_id || row.creditOwnerId ? ` #${row.credit_owner_id || row.creditOwnerId}` : ''}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-900 font-semibold">
                  {formatMoney(row.currency, row.amount)}
                </td>
                <td className="px-4 py-3 text-slate-600">{row.memo || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <button
          type="button"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          disabled={pagination.page <= 1 || loading}
          onClick={() => load(Math.max(1, pagination.page - 1))}
        >
          Previous
        </button>
        <span>
          Page {pagination.page} of {pageCount}
        </span>
        <button
          type="button"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          disabled={pagination.page >= pageCount || loading}
          onClick={() => load(Math.min(pageCount, pagination.page + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdminLedgerEntries;
